import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Card,
  CardContent,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Description,
  Visibility,
  Download,
  Cancel,
  Search,
  Refresh,
  ContentCopy,
  Close,
  QrCode2,
  PictureAsPdf,
  OpenInNew,
} from '@mui/icons-material';
import api from '../../services/api';

interface Boleto {
  nossoNumero: number;
  seuNumero?: string;
  linhaDigitavel?: string;
  codigoBarras?: string;
  situacao: number;
  situacaoBoleto?: string;
  dataEmissao?: string;
  dataVencimento: string;
  dataLimitePagamento?: string;
  valor: number;
  valorNominal?: number;
  valorAbatimento?: number;
  valorTotalRecebido?: number;
  dataPagamento?: string;
  pagador: {
    numeroCpfCnpj: string;
    nome: string;
    email?: string;
    endereco?: string;
    bairro?: string;
    cidade?: string;
    cep?: string;
    uf?: string;
  };
  mensagens?: {
    tipoInstrucao: number;
    mensagem: string;
  }[];
  mensagensInstrucao?: string[];
  pdfBoleto?: string;
  qrCode?: string;
}

const SITUACOES = [
  { value: 0, label: 'Todas' },
  { value: 1, label: 'Em Aberto' },
  { value: 2, label: 'Baixado' },
  { value: 3, label: 'Liquidado' },
];

const MAX_DAYS_RANGE = 35;

// Helper to format date as yyyy-MM-dd
const formatDateInput = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// Helper to get default date range (last 35 days)
const getDefaultDates = () => {
  const today = new Date();
  const thirtyFiveDaysAgo = new Date();
  thirtyFiveDaysAgo.setDate(today.getDate() - MAX_DAYS_RANGE);
  return {
    start: formatDateInput(thirtyFiveDaysAgo),
    end: formatDateInput(today),
  };
};

// Helper to calculate days between two dates
const getDaysDifference = (start: string, end: string): number => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const BoletoListPage: React.FC = () => {
  const navigate = useNavigate();
  const defaultDates = getDefaultDates();
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [situacao, setSituacao] = useState<number>(0);
  const [dataInicio, setDataInicio] = useState(defaultDates.start);
  const [dataFim, setDataFim] = useState(defaultDates.end);

  const [boletos, setBoletos] = useState<Boleto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [selectedBoleto, setSelectedBoleto] = useState<Boleto | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [baixarLoading, setBaixarLoading] = useState(false);
  const [baixarConfirmOpen, setBaixarConfirmOpen] = useState(false);
  const [boletoToBaixar, setBoletoToBaixar] = useState<Boleto | null>(null);

  const formatCpfCnpj = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const handleCpfCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCpfCnpj(e.target.value);
    if (formatted.replace(/\D/g, '').length <= 14) {
      setCpfCnpj(formatted);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  const getStatusChip = (boleto: Boleto) => {
    // Sicoob can return situacaoBoleto as string or situacao as number
    const status = boleto.situacaoBoleto?.toUpperCase() || '';
    const code = boleto.situacao;

    if (status === 'BAIXADO' || code === 2) {
      return <Chip label="Baixado" color="default" size="small" />;
    }
    if (status === 'LIQUIDADO' || code === 3) {
      return <Chip label="Liquidado" color="success" size="small" />;
    }
    if (status === 'EM ABERTO' || status === 'EMABERTO' || code === 1) {
      return <Chip label="Em Aberto" color="warning" size="small" />;
    }
    if (status === 'VENCIDO') {
      return <Chip label="Vencido" color="error" size="small" />;
    }
    return <Chip label={status || `Status ${code}`} size="small" />;
  };

  // Auto-adjust dates when one changes to maintain max 35 days range
  const handleDataInicioChange = (value: string) => {
    setDataInicio(value);
    if (value && dataFim) {
      const diff = getDaysDifference(value, dataFim);
      if (diff > MAX_DAYS_RANGE) {
        // Adjust dataFim to be 35 days after dataInicio
        const newEndDate = new Date(value);
        newEndDate.setDate(newEndDate.getDate() + MAX_DAYS_RANGE);
        setDataFim(formatDateInput(newEndDate));
      }
    }
  };

  const handleDataFimChange = (value: string) => {
    setDataFim(value);
    if (value && dataInicio) {
      const diff = getDaysDifference(dataInicio, value);
      if (diff > MAX_DAYS_RANGE) {
        // Adjust dataInicio to be 35 days before dataFim
        const newStartDate = new Date(value);
        newStartDate.setDate(newStartDate.getDate() - MAX_DAYS_RANGE);
        setDataInicio(formatDateInput(newStartDate));
      }
    }
  };

  const handleSearch = async () => {
    if (!cpfCnpj || cpfCnpj.replace(/\D/g, '').length < 11) {
      setError('Informe um CPF ou CNPJ válido para pesquisar');
      return;
    }

    // Validate date range
    if (dataInicio && dataFim) {
      const diff = getDaysDifference(dataInicio, dataFim);
      if (diff > MAX_DAYS_RANGE) {
        setError(`O período não pode ser maior que ${MAX_DAYS_RANGE} dias`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const numeroCpfCnpj = cpfCnpj.replace(/\D/g, '');

      const params: Record<string, string | number> = {};
      if (situacao > 0) {
        params.codigoSituacao = situacao;
      }
      if (dataInicio) {
        params.dataInicio = dataInicio;
      }
      if (dataFim) {
        params.dataFim = dataFim;
      }

      const response = await api.get(`/boletos/pagador/${numeroCpfCnpj}`, { params });

      if (response.success && response.data) {
        // API returns data directly or in resultado.boletos
        const boletoData = response.data.resultado?.boletos || response.data.boletos || response.data || [];
        setBoletos(Array.isArray(boletoData) ? boletoData : [boletoData]);
      } else {
        setBoletos([]);
        if (response.error?.message) {
          setError(response.error.message);
        }
      }
    } catch (err: any) {
      console.error('Erro ao buscar boletos:', err);
      setBoletos([]);
      // Handle error from API service
      const errorMessage = err.error?.message || err.message || 'Erro ao buscar boletos';
      if (errorMessage.includes('404') || errorMessage.includes('encontrado')) {
        setError('Nenhum boleto encontrado para este CPF/CNPJ');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (boleto: Boleto) => {
    setSelectedBoleto(boleto);
    setDetailsOpen(true);
  };

  const handleDownloadPdf = async (boleto: Boleto) => {
    try {
      const response = await api.get(`/boletos/${boleto.nossoNumero}/segunda-via`, {
        params: { gerarPdf: true }
      });

      if (response.data.success && response.data.data?.pdfBoleto) {
        const pdfData = response.data.data.pdfBoleto;
        const byteCharacters = atob(pdfData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `boleto_${boleto.nossoNumero}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('PDF não disponível');
      }
    } catch (err: any) {
      console.error('Erro ao baixar PDF:', err);
      setError(err.response?.data?.error?.message || 'Erro ao baixar PDF');
    }
  };

  const handleBaixarClick = (boleto: Boleto) => {
    setBoletoToBaixar(boleto);
    setBaixarConfirmOpen(true);
  };

  const handleConfirmBaixar = async () => {
    if (!boletoToBaixar) return;

    setBaixarLoading(true);
    try {
      await api.post(`/boletos/${boletoToBaixar.nossoNumero}/baixar`, {
        seuNumero: boletoToBaixar.seuNumero,
      });

      // Atualizar lista
      handleSearch();
      setBaixarConfirmOpen(false);
      setBoletoToBaixar(null);
    } catch (err: any) {
      console.error('Erro ao baixar boleto:', err);
      setError(err.response?.data?.error?.message || 'Erro ao baixar boleto');
    } finally {
      setBaixarLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
        Consultar Boletos
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Busque boletos pelo CPF ou CNPJ do pagador
        </Typography>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="CPF/CNPJ do Pagador *"
              value={cpfCnpj}
              onChange={handleCpfCnpjChange}
              placeholder="000.000.000-00"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Situação</InputLabel>
              <Select
                value={situacao}
                label="Situação"
                onChange={(e) => setSituacao(Number(e.target.value))}
              >
                {SITUACOES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Data Início"
              type="date"
              value={dataInicio}
              onChange={(e) => handleDataInicioChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              helperText="Máx. 35 dias"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              value={dataFim}
              onChange={(e) => handleDataFimChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {hasSearched && boletos.length === 0 && !loading && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nenhum boleto encontrado para os filtros informados.
        </Alert>
      )}

      {boletos.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {boletos.length} boleto(s) encontrado(s)
            </Typography>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={handleSearch}
              disabled={loading}
            >
              Atualizar
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell>Nosso Número</TableCell>
                  <TableCell>Seu Número</TableCell>
                  <TableCell>Pagador</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Vencimento</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {boletos.map((boleto) => (
                  <TableRow key={boleto.nossoNumero} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {boleto.nossoNumero}
                      </Typography>
                    </TableCell>
                    <TableCell>{boleto.seuNumero || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {boleto.pagador?.nome || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCpfCnpj(boleto.pagador?.numeroCpfCnpj || '')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(boleto.valor || boleto.valorNominal || 0)}
                      </Typography>
                      {boleto.valorTotalRecebido && boleto.valorTotalRecebido !== boleto.valorNominal && (
                        <Typography variant="caption" color="success.main">
                          Pago: {formatCurrency(boleto.valorTotalRecebido)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(boleto.dataVencimento)}
                      {boleto.dataPagamento && (
                        <Typography variant="caption" display="block" color="success.main">
                          Pago em: {formatDate(boleto.dataPagamento)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(boleto)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver resumo">
                        <IconButton size="small" onClick={() => handleViewDetails(boleto)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Abrir página completa">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/boletos/${boleto.nossoNumero}`)}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download PDF">
                        <IconButton size="small" onClick={() => handleDownloadPdf(boleto)}>
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(boleto.situacao === 1 || boleto.situacaoBoleto?.toUpperCase() === 'EM ABERTO') && (
                        <Tooltip title="Baixar boleto">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleBaixarClick(boleto)}
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Dialog de Detalhes */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Detalhes do Boleto</Typography>
            <Typography variant="caption" color="text.secondary">
              Nosso Número: {selectedBoleto?.nossoNumero}
            </Typography>
          </Box>
          <IconButton onClick={() => setDetailsOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBoleto && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Linha Digitável
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontFamily="monospace" sx={{ flex: 1 }}>
                        {selectedBoleto.linhaDigitavel || 'Não disponível'}
                      </Typography>
                      {selectedBoleto.linhaDigitavel && (
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(selectedBoleto.linhaDigitavel!)}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Dados do Boleto
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Nosso Número:</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedBoleto.nossoNumero}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Seu Número:</Typography>
                        <Typography variant="body2">{selectedBoleto.seuNumero || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Data Emissão:</Typography>
                        <Typography variant="body2">{formatDate(selectedBoleto.dataEmissao)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Data Vencimento:</Typography>
                        <Typography variant="body2">{formatDate(selectedBoleto.dataVencimento)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Limite Pagamento:</Typography>
                        <Typography variant="body2">{formatDate(selectedBoleto.dataLimitePagamento)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Situação:</Typography>
                        {getStatusChip(selectedBoleto)}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Valores
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Valor Nominal:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(selectedBoleto.valor || selectedBoleto.valorNominal || 0)}
                        </Typography>
                      </Box>
                      {selectedBoleto.valorAbatimento && selectedBoleto.valorAbatimento > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Abatimento:</Typography>
                          <Typography variant="body2" color="error.main">
                            - {formatCurrency(selectedBoleto.valorAbatimento)}
                          </Typography>
                        </Box>
                      )}
                      {selectedBoleto.valorTotalRecebido && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Valor Recebido:</Typography>
                          <Typography variant="body2" color="success.main" fontWeight="medium">
                            {formatCurrency(selectedBoleto.valorTotalRecebido)}
                          </Typography>
                        </Box>
                      )}
                      {selectedBoleto.dataPagamento && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Data Pagamento:</Typography>
                          <Typography variant="body2" color="success.main">
                            {formatDate(selectedBoleto.dataPagamento)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Dados do Pagador
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Nome:</Typography>
                        <Typography variant="body2">{selectedBoleto.pagador?.nome || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">CPF/CNPJ:</Typography>
                        <Typography variant="body2">
                          {selectedBoleto.pagador?.numeroCpfCnpj
                            ? formatCpfCnpj(selectedBoleto.pagador.numeroCpfCnpj)
                            : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">E-mail:</Typography>
                        <Typography variant="body2">{selectedBoleto.pagador?.email || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Endereço:</Typography>
                        <Typography variant="body2">
                          {selectedBoleto.pagador?.endereco
                            ? `${selectedBoleto.pagador.endereco}, ${selectedBoleto.pagador.bairro || ''} - ${selectedBoleto.pagador.cidade || ''}/${selectedBoleto.pagador.uf || ''} - CEP: ${selectedBoleto.pagador.cep || ''}`
                            : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {selectedBoleto.mensagens && selectedBoleto.mensagens.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Instruções
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      {selectedBoleto.mensagens.map((msg, idx) => (
                        <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                          • {msg.mensagem}
                        </Typography>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {selectedBoleto.qrCode && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        <QrCode2 sx={{ mr: 1, verticalAlign: 'middle' }} />
                        QR Code PIX
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <img
                        src={`data:image/png;base64,${selectedBoleto.qrCode}`}
                        alt="QR Code PIX"
                        style={{ maxWidth: 200 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<PictureAsPdf />}
            onClick={() => selectedBoleto && handleDownloadPdf(selectedBoleto)}
          >
            Download PDF
          </Button>
          <Button onClick={() => setDetailsOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de baixa */}
      <Dialog open={baixarConfirmOpen} onClose={() => setBaixarConfirmOpen(false)}>
        <DialogTitle>Confirmar Baixa do Boleto</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta ação não pode ser desfeita. O boleto será baixado no sistema bancário.
          </Alert>
          <Typography variant="body2">
            Deseja realmente dar baixa no boleto <strong>{boletoToBaixar?.nossoNumero}</strong>?
          </Typography>
          {boletoToBaixar && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Pagador: {boletoToBaixar.pagador?.nome}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valor: {formatCurrency(boletoToBaixar.valor || boletoToBaixar.valorNominal || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vencimento: {formatDate(boletoToBaixar.dataVencimento)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBaixarConfirmOpen(false)} disabled={baixarLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmBaixar}
            disabled={baixarLoading}
            startIcon={baixarLoading ? <CircularProgress size={20} /> : <Cancel />}
          >
            {baixarLoading ? 'Processando...' : 'Confirmar Baixa'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoletoListPage;
